import * as TronGrid from "trongrid";
import * as TronWeb from "tronweb";
import { TRON_PRIVATE_KEY } from "../constants/config";
import ITronWeb from "../types/tronweb";

export interface IEventsOptions {
  only_confirmed?: boolean;
  only_unconfirmed?: boolean;
  event_name?: string;
  block_number?: number;
  min_timestamp?: number;
  max_timestamp?: number;
  order_by?: "timestamp, asc" | "timestamp, desc" | any;
  limit?: number; // Default 20, Max 200
  fingerprint?: string;
}

interface IEventsOptions2 {
  onlyConfirmed?: boolean;
  onlyUnconfirmed?: boolean;
  eventName?: string;
  blockNumber?: number;
  fromTimestamp?: number;
  sort?: "timestamp, asc" | "timestamp, desc" | any;
  size?: number; // Default 20, Max 200
  previousFingerprint?: string;
}

export interface IEvent {
  block_timestamp: number;
  contract_address: string;
  event_name: string;
  block_number: number;
  event_index: number;
  result: any;
  result_type: any;
  transaction_id: string;
  caller_contract_address: string;
}

interface IContractEventsResponse {
  success: boolean;
  meta: {
    at: number;
    page_size: number;
    fingerprint?: string;
    links?: { next: string };
  };
  data: IEvent[];
}

export interface IAccount {
  privateKey: string;
  publicKey: string;
  address: {
    base58: string;
    hex: string;
  };
}

export interface ITronTransaction {
  ret: [{ contractRet: "SUCCESS" | "REVERT" | string }];
  signature: [string];
  txID: string;
  raw_data: {
    contract: [
      {
        parameter: any;
        type: "TriggerSmartContract" | "TransferContract" | string;
      }
    ];
    ref_block_bytes: string;
    ref_block_hash: string;
    expiration: number;
    timestamp: number;
    fee_limit: number | undefined;
  };
  raw_data_hex: string;
}

export interface IAccountResources {
  TotalEnergyLimit: number;
  TotalEnergyWeight: number;
  TotalNetLimit: number;
  TotalNetWeight: number;
  assetNetLimit: any[];
  assetNetUsed: any[];
  freeNetLimit: number;
}

export class TronService {
  tronWeb: ITronWeb;
  tronGrid: any;

  constructor(fullHost: string, fullNode: string, solidityNode: string) {
    // fullhost provides fullNode, solidityNode, eventServer
    this.tronWeb = new TronWeb({
      fullHost: new TronWeb.providers.HttpProvider(fullHost, 2 * 60 * 1000),
      // fullNode: new TronWeb.providers.HttpProvider(fullNode, 2 * 60 * 1000),
      // solidityNode: new TronWeb.providers.HttpProvider(solidityNode, 2 * 60 * 1000),
      privateKey: TRON_PRIVATE_KEY,
    });

    this.tronGrid = new TronGrid(this.tronWeb);
  }

  getTrxBalance(address: string) {
    return this.tronWeb.trx.getBalance(address);
  }

  contract(address: string): Promise<any> {
    return this.tronWeb.contract().at(address);
  }

  toDecimal(hexString: string) {
    return this.tronWeb.toDecimal(hexString);
  }

  fromSun(amount: number) {
    return this.tronWeb.fromSun(amount);
  }

  isAddress(address: string): boolean {
    return this.tronWeb.isAddress(address);
  }

  createAccount(): Promise<IAccount> {
    return this.tronWeb.createAccount();
  }

  async getAccountResources(
    address: string
  ): Promise<{
    account: any;
    balance: number;
    accountResources: IAccountResources;
    bandwidth: number;
  }> {
    const account = await this.tronWeb.trx.getAccount(address);
    const balance = await this.tronWeb.trx.getBalance(address);
    const accountResources = await this.tronWeb.trx.getAccountResources(
      address
    );
    const bandwidth = await this.tronWeb.trx.getBandwidth(address);

    return { account, balance, accountResources, bandwidth };
  }

  setAddress(address: string) {
    this.tronWeb.setAddress(address);
  }

  toHex(value: string | number) {
    return this.tronWeb.toHex(value);
  }

  toBigNumber(value: number) {
    return this.tronWeb.toBigNumber(value);
  }

  async getTx(txid: string): Promise<ITronTransaction> {
    return this.tronWeb.trx.getTransaction(txid);
  }

  async getTxInfo(txid: string) {
    return this.tronWeb.trx.getTransactionInfo(txid);
  }

  async getAllEvents(
    contractAddress: string,
    options: IEventsOptions | IEventsOptions2 | any,
    max: number = 1000
  ): Promise<IEvent[]> {
    let events: IEvent[] = [];
    let count = 0;
    let response: any;

    do {
      response = await this.getEvents(contractAddress, options);
      events = events.concat(response.data);
      count += options.limit;
      options.fingerprint = response.meta.fingerprint || undefined;
    } while (response.meta.fingerprint && count < max);

    return events;
  }

  async getEvents(
    contractAddress: string,
    options?: IEventsOptions | IEventsOptions2 | any
  ): Promise<IContractEventsResponse> {
    // tslint:disable-next-line
    // https://api.trongrid.io/event/contract/TEEXEWrkMFKapSMJ6mErg39ELFKDqEs6w3?fromTimestamp=1551779730000&sort=-block_timestamp

    // tslint:disable-next-line
    // https://api.shasta.trongrid.io/event/contract/TRbqKMRmcToCAdWo55nNT5VQSyAxHubRVW/QuarantineBurnt/latest?size=20&page=1&since=1577265432834&fromTimestamp=1577265432834&sort=block_timestamp

    return this.tronGrid.contract.getEvents(contractAddress, options);
  }

  async getAccountTrc20Transactions(
    address: string,
    options: any,
    max: number = 1000
  ): Promise<any[]> {
    let transactions: any[] = [];
    let count = 0;
    let response: any;

    do {
      response = await this.getTrc20Transactions(address, options);
      transactions = transactions.concat(response.data);
      count += options.limit;
      options.fingerprint = response.meta.fingerprint || undefined;
    } while (response.meta.fingerprint && count < max);

    return transactions;
  }

  async getTrc20Transactions(address: string, options?: any): Promise<any> {
    return this.tronGrid.account.getTrc20Transactions(address, options);
  }

  async getAccountInfo(address: string, options: any): Promise<any> {
    const { data } = await this.getInfo(address, options);
    return data;
  }

  async getInfo(address: string, options?: any): Promise<any> {
    return this.tronGrid.account.get(address, options);
  }
}
